/**
* ActionHistory.js - Panel lateral de historial de acciones
*/
import React from 'react';
import PropTypes from 'prop-types';

const ActionHistory = ({ 
 history = [], 
 maxItems = 15,
 onClear,
 className = "" 
}) => {
 
 // Formatear tiempo relativo
 const formatTime = (time) => {
   if (!time) return '';
   
   // Si es un timestamp
   if (time instanceof Date) {
     const now = new Date();
     const diff = now - time;
     const minutes = Math.floor(diff / 60000);
     
     if (minutes < 1) return 'ahora';
     if (minutes < 60) return `hace ${minutes} min`;
     
     const hours = Math.floor(minutes / 60);
     if (hours < 24) return `hace ${hours} hr${hours > 1 ? 's' : ''}`;
     
     const days = Math.floor(hours / 24);
     return `hace ${days} día${days > 1 ? 's' : ''}`;
   }
   
   // Si ya viene formateado
   return time;
 };

 // Obtener ícono según el tipo de acción
 const getActionIcon = (action) => {
   if (action.includes('Búsqueda')) return 'bi-search';
   if (action.includes('Plan')) return 'bi-tag';
   if (action.includes('Vigencia')) return 'bi-calendar-plus';
   if (action.includes('Filtro')) return 'bi-funnel';
   if (action.includes('exportado')) return 'bi-download';
   if (action.includes('creado')) return 'bi-plus-circle';
   if (action.includes('Impreso')) return 'bi-printer';
   if (action.includes('Entregado')) return 'bi-truck';
   if (action.includes('Activado')) return 'bi-check-circle';
   if (action.includes('Perdido')) return 'bi-exclamation-triangle';
   if (action.includes('Encontrado')) return 'bi-check2-circle';
   return 'bi-clock-history';
 };

 // Obtener color según el tipo
 const getActionColor = (action) => {
   if (action.includes('Error')) return 'text-danger';
   if (action.includes('Perdido')) return 'text-danger';
   if (action.includes('Vigencia')) return 'text-success';
   if (action.includes('Plan')) return 'text-info';
   if (action.includes('exportado')) return 'text-primary';
   return 'text-muted';
 };

 const visibleHistory = history.slice(0, maxItems);

 return (
   <div className={`history-panel card shadow-sm ${className}`}>
     <div className="card-header bg-white d-flex justify-content-between align-items-center">
       <h6 className="mb-0">
         <i className="bi bi-clock-history me-2"></i>
         Historial de Sesión
       </h6>
       {onClear && history.length > 0 && (
         <button 
           className="btn btn-link btn-sm p-0 text-muted"
           onClick={onClear}
           title="Limpiar historial"
         >
           <i className="bi bi-trash"></i>
         </button>
       )}
     </div>
     
     <div className="card-body p-0">
       {history.length === 0 ? (
         <div className="text-center py-4 text-muted">
           <i className="bi bi-clock-history fs-3 mb-2 d-block"></i>
           <small>Sin actividad reciente</small>
         </div>
       ) : (
         <div className="history-list">
           {visibleHistory.map((item) => (
             <div 
               key={item.id} 
               className="history-item p-3 border-bottom position-relative"
             >
               {/* Ícono de acción */}
               <div className="d-flex align-items-start">
                 <i className={`${getActionIcon(item.action)} ${getActionColor(item.action)} me-2 mt-1`}></i>
                 
                 <div className="flex-grow-1 overflow-hidden">
                   {/* Acción */}
                   <small className="d-block fw-semibold text-truncate">
                     {item.action}
                   </small>
                   
                   {/* Usuario y tiempo */}
                   <small className="text-muted">
                     {item.user && (
                       <>
                         <i className="bi bi-person me-1"></i>
                         {item.user}
                         {item.time && ' • '}
                       </>
                     )}
                     {item.time && formatTime(item.time)}
                   </small>
                   
                   {/* Detalles adicionales si existen */}
                   {item.details && (
                     <small className="d-block text-muted mt-1">
                       {item.details}
                     </small>
                   )}
                 </div>
               </div>
             </div>
           ))}
           
           {/* Indicador de más elementos */}
           {history.length > maxItems && (
             <div className="text-center py-2 text-muted border-top">
               <small>
                 Mostrando {maxItems} de {history.length} acciones
               </small>
             </div>
           )}
         </div>
       )}
     </div>
   </div>
 );
};

ActionHistory.propTypes = {
 history: PropTypes.arrayOf(PropTypes.shape({
   id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
   action: PropTypes.string.isRequired,
   user: PropTypes.string,
   time: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
   details: PropTypes.string
 })),
 maxItems: PropTypes.number,
 onClear: PropTypes.func,
 className: PropTypes.string
};

export default ActionHistory;